/**
 * filterBuilder.ts - Part 1
 * Centralized filter request builder used across all endpoints.
 */

import type {
  AppliedFiltersSnapshot,
  StandardFilterRequest,
  KpiFilterRequest,
  LocationSummaryFilterRequest,
  LoadExecutionFilterRequest,
} from '../types/filterTypes';
import { resolveLocationIdsFromFilters } from './locationFilterHelpers';
import { getDateRangeFromQuickSelect, addOneDayToIsoDate } from './timeFrameHelpers';
import type { CurrentFiscalContext } from '../../../../../api/periodWeeksApi';

function extractYears(selectedYears: { id: string; displayText: string }[]): number[] {
  return selectedYears
    .map((y) => Number(y.id))
    .filter((n) => Number.isFinite(n));
}

function extractPeriods(selectedPeriods: { id: string; displayText: string }[]): number[] {
  return selectedPeriods
    .map((p) => {
      const raw = String(p.id).replace(/^P/i, '');
      const num = Number(raw);
      return Number.isFinite(num) ? num : null;
    })
    .filter((n): n is number => n !== null);
}

function extractWeeks(selectedWeeks: { id: string; displayText: string }[]): number[] {
  return selectedWeeks
    .map((w) => {
      const raw = String(w.id).replace(/^W/i, '');
      const num = Number(raw);
      return Number.isFinite(num) ? num : null;
    })
    .filter((n): n is number => n !== null);
}

function extractDeliveryDateRange(
  appliedFilters: AppliedFiltersSnapshot,
  currentFiscalContext?: CurrentFiscalContext
): { start?: string; end?: string } {
  // Backend's endDate is an exclusive upper bound, so the inclusive end date
  // computed here must be advanced by one day before being sent to the API.
  if (appliedFilters.activeQuickSelect) {
    try {
      const range = getDateRangeFromQuickSelect(
        appliedFilters.activeQuickSelect,
        currentFiscalContext
      );
      return { start: range.start, end: addOneDayToIsoDate(range.end) };
    } catch (err) {
      console.warn('[FilterBuilder] QuickSelect conversion failed:', err);
    }
  }

  if (appliedFilters.deliveryDateRange.start) {
    const end = appliedFilters.deliveryDateRange.end || appliedFilters.deliveryDateRange.start;
    return {
      start: appliedFilters.deliveryDateRange.start,
      end: addOneDayToIsoDate(end),
    };
  }

  return {};
}

export function buildStandardFilterRequest(
  appliedFilters: AppliedFiltersSnapshot,
  adminLocations: any[],
  currentFiscalContext?: CurrentFiscalContext
): StandardFilterRequest {
  const locationIds = resolveLocationIdsFromFilters(
    {
      selectedRegions: appliedFilters.selectedRegions,
      selectedMarkets: appliedFilters.selectedMarkets,
      selectedLocations: appliedFilters.selectedLocations,
    },
    adminLocations
  );

  const years = extractYears(appliedFilters.selectedYears);
  const periods = extractPeriods(appliedFilters.selectedPeriods);
  const weeks = extractWeeks(appliedFilters.selectedWeeks);
  const dateRange = extractDeliveryDateRange(appliedFilters, currentFiscalContext);

  const request: StandardFilterRequest = {};

  if (locationIds.length > 0) request.location = locationIds;
  if (years.length > 0) request.year = years;
  if (periods.length > 0) request.period = periods;
  if (weeks.length > 0) request.week = weeks;
  if (dateRange.start) request.deliveryDateStart = dateRange.start;
  if (dateRange.end) request.deliveryDateEnd = dateRange.end;

  return request;
}

export function buildKpiFilterRequest(
  appliedFilters: AppliedFiltersSnapshot,
  adminLocations: any[],
  currentFiscalContext?: CurrentFiscalContext
): KpiFilterRequest {
  const base = buildStandardFilterRequest(appliedFilters, adminLocations, currentFiscalContext);
  const locationTypes = appliedFilters.selectedLocationTypes
    .map((lt) => String(lt.id))
    .filter((id) => id);

  return {
    ...base,
    ...(locationTypes.length > 0 && { locationType: locationTypes }),
  };
}

export function buildLocationSummaryFilterRequest(
  appliedFilters: AppliedFiltersSnapshot,
  adminLocations: any[],
  grainColumns: string[] = ['region'],
  currentFiscalContext?: CurrentFiscalContext
): LocationSummaryFilterRequest {
  const base = buildStandardFilterRequest(appliedFilters, adminLocations, currentFiscalContext);
  const locationTypes = appliedFilters.selectedLocationTypes
    .map((lt) => String(lt.id))
    .filter((id) => id);

  return {
    ...base,
    ...(locationTypes.length > 0 && { locationType: locationTypes }),
    grainColumns,
  };
}

export function buildLoadExecutionFilterRequest(
  appliedFilters: AppliedFiltersSnapshot,
  adminLocations: any[],
  currentFiscalContext?: CurrentFiscalContext
): LoadExecutionFilterRequest {
  const base = buildStandardFilterRequest(appliedFilters, adminLocations, currentFiscalContext);

  return {
    ...base,
    driver: appliedFilters.selectedDrivers?.map((d) => String(d.id)) || [],
    status: appliedFilters.selectedStatuses?.map((s) => String(s.id)) as any,
    onTime: appliedFilters.selectedOnTime?.map((ot) => ot.displayText === 'On Time') || [],
    arrivedAsExpected: appliedFilters.selectedArrivedAsExpected?.map((ae) => String(ae.id)) as any,
    manifestHourStart: appliedFilters.manifestHourRange?.from,
    manifestHourEnd: appliedFilters.manifestHourRange?.to,
    searchText: appliedFilters.loadSearch?.trim(),
  };
}
